import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'

import type { FreeTable } from '@/features/checkin/types'
import { apiClient } from '@/lib/apiClient'
import { accessToken } from '@/lib/staffSession'

import type { PlacementBoard, PlacementHistoryEntry, PlacementRow } from './types'

/**
 * Le placement des tables — une seule API pour l'onglet « Placement » de
 * l'écran Salle et pour la page du back-office.
 *
 * Comme toutes les surfaces `/ops`, chaque appel porte le jeton **et**
 * l'en-tête d'édition.
 */
async function authorized(eventId: string | undefined) {
  return { token: await accessToken(), ...(eventId ? { eventId } : {}) }
}

/** L'arrivée vient des check-ins de la porte : l'écran se relit seul. */
export const PLACEMENT_REFRESH_MS = 10_000

export function usePlacementBoard(eventId: string | undefined, enabled: boolean) {
  const { i18n } = useTranslation()
  return useQuery({
    queryKey: ['placement-board', eventId, i18n.language],
    queryFn: async () =>
      apiClient.get<PlacementBoard>(
        `/ops/placement?locale=${i18n.language}`,
        await authorized(eventId),
      ),
    enabled: Boolean(eventId) && enabled,
    refetchInterval: PLACEMENT_REFRESH_MS,
  })
}

export function usePlacementFreeTables(reservationId: string | null, eventId: string | undefined) {
  return useQuery({
    queryKey: ['placement-free-tables', reservationId, eventId],
    queryFn: async () =>
      apiClient.get<FreeTable[]>(
        `/ops/placement/${reservationId}/free-tables`,
        await authorized(eventId),
      ),
    enabled: Boolean(reservationId) && Boolean(eventId),
    staleTime: 0,
  })
}

export function usePlacementHistory(reservationId: string | null, eventId: string | undefined) {
  return useQuery({
    queryKey: ['placement-history', reservationId, eventId],
    queryFn: async () =>
      apiClient.get<PlacementHistoryEntry[]>(
        `/ops/placement/${reservationId}/history`,
        await authorized(eventId),
      ),
    enabled: Boolean(reservationId) && Boolean(eventId),
  })
}

/** Tout ce qui bouge quand un groupe change de table ou s'assoit. */
function useInvalidatePlacement() {
  const queryClient = useQueryClient()
  return () => {
    for (const key of [
      ['placement-board'],
      ['placement-free-tables'],
      ['placement-history'],
      ['free-tables'],
      ['floor-plan'],
    ]) {
      void queryClient.invalidateQueries({ queryKey: key })
    }
  }
}

type Action =
  | { kind: 'table'; reservationId: string; physical_table_id: string | null; reason?: string | null }
  | { kind: 'seat'; reservationId: string }
  | { kind: 'unseat'; reservationId: string; reason: string }

export function usePlacementAction(eventId: string | undefined) {
  const { i18n } = useTranslation()
  const invalidate = useInvalidatePlacement()
  return useMutation({
    mutationFn: async (action: Action) => {
      const options = await authorized(eventId)
      const base = `/ops/placement/${action.reservationId}`
      const locale = `?locale=${i18n.language}`
      if (action.kind === 'table') {
        return apiClient.post<PlacementRow>(
          `${base}/table${locale}`,
          { physical_table_id: action.physical_table_id, reason: action.reason ?? null },
          options,
        )
      }
      if (action.kind === 'seat') {
        return apiClient.post<PlacementRow>(`${base}/seat${locale}`, {}, options)
      }
      return apiClient.post<PlacementRow>(
        `${base}/unseat${locale}`,
        { reason: action.reason },
        options,
      )
    },
    onSettled: invalidate,
  })
}
